import { useMutation, useQueryClient } from "@tanstack/react-query";
import { produce } from "immer";
import { toast } from "react-toastify";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import getQueryKeys from "common/datahooks/getQueryKeys";
import useUpdateConversationParticipant from "common/datahooks/useUpdateConversationParticipant";
import useUpdateLead from "common/datahooks/useUpdateLead";
import { del, post } from "common/helpers/HTTP";
import type { FacetParam, Label, Lead } from "common/types";

import useUpdateDetailedLead from "./useUpdateDetailedLead";

async function addLabel(workspaceId: string, profileId: string, label: Label) {
  await post(`workspaces/${workspaceId}/leads/${profileId}/labels`, {
    labels: [label.id],
  });
}
async function removeLabel(
  workspaceId: string,
  profileId: string,
  label: Label,
) {
  await del(`workspaces/${workspaceId}/leads/${profileId}/labels/${label.id}`);
}

export default function useLeadLabelsMutations() {
  const queryClient = useQueryClient();
  const { id: workspaceId } = useSelectedWorkspaceContext();
  const { leadsKeys } = getQueryKeys(workspaceId);
  const { updateLead } = useUpdateLead();
  const { updateDetailedLead } = useUpdateDetailedLead();
  const { updateConversationParticipant } = useUpdateConversationParticipant();

  async function updateCachedLead(
    leadId: string,
    updateFunction: (lead: Pick<Lead, "labels">) => void,
  ) {
    await updateLead(leadId, updateFunction);
    await updateDetailedLead(leadId, updateFunction);
    await updateConversationParticipant(leadId, updateFunction);
  }

  const { mutateAsync: addLeadLabel, isPending: isAddingLeadLabel } =
    useMutation({
      mutationFn: ({ profileId, label }: { profileId: string; label: Label }) =>
        addLabel(workspaceId, profileId, label),
      onMutate: async ({ profileId, label }) => {
        await updateCachedLead(profileId, (lead) => {
          lead.labels.push(label);
        });
        queryClient.setQueriesData<FacetParam[]>(
          { queryKey: leadsKeys.facet("labels") },
          (queryData) => {
            return produce(queryData, (draftFacetData) => {
              for (let i = 0; i < draftFacetData.length; i += 1) {
                if (label.id === draftFacetData[i].id) {
                  draftFacetData[i].target_count += 1;
                  return;
                }
              }
              return [
                ...draftFacetData,
                { id: label.id, name: label.name, target_count: 1 },
              ];
            });
          },
        );
      },
      onError: async (_, { profileId, label }) => {
        await updateCachedLead(profileId, (lead) => {
          lead.labels = lead.labels.filter(({ id }) => label.id !== id);
        });

        toast.error("Error adding label");
      },
    });
  const { mutateAsync: removeLeadLabel, isPending: isRemovingLeadLabel } =
    useMutation({
      mutationFn: ({ profileId, label }: { profileId: string; label: Label }) =>
        removeLabel(workspaceId, profileId, label),
      onMutate: async ({ profileId, label }) => {
        await updateCachedLead(profileId, (lead) => {
          lead.labels = lead.labels.filter(({ id }) => id !== label.id);
        });

        queryClient.setQueriesData<FacetParam[]>(
          { queryKey: leadsKeys.facet("labels") },
          (queryData) => {
            return produce(queryData, (draftFacetData) => {
              for (let i = 0; i < draftFacetData.length; i += 1) {
                if (label.id === draftFacetData[i].id) {
                  if (draftFacetData[i].target_count === 1) {
                    return draftFacetData.filter(({ id }) => id !== label.id);
                  } else {
                    draftFacetData[i].target_count -= 1;
                    return;
                  }
                }
              }
            });
          },
        );
        return label;
      },
      onError: async (_, { profileId, label }) => {
        await updateCachedLead(profileId, (lead) => {
          lead.labels.push(label);
        });

        toast.error("Error removing label");
      },
    });
  return {
    addLeadLabel,
    isAddingLeadLabel,
    removeLeadLabel,
    isRemovingLeadLabel,
  };
}
